<template>
  <div
    class="content-wrapper view-setting p-0 fm"
    :class="{ 'fm--selecting': selecting || selected.length > 0 }"
  >
    <div class="fm-page">
      <header class="fm-head">
        <div class="fm-title">
          <div class="fm-title-row">
            <h1>จัดการไฟล์</h1>
            <span v-if="loaded" class="fm-pill">{{ total }} ไฟล์</span>
            <span class="flex-grow-1"></span>
            <button
              type="button"
              class="fm-link-btn fm-mob"
              @click="toggleSelecting"
            >
              {{ selecting ? "เสร็จ" : "เลือก" }}
            </button>
          </div>
          <p>รูป เอกสาร และแบบฟอร์มของทุกคนไข้ในคลินิก</p>
        </div>

        <section class="fm-storage" aria-label="พื้นที่เก็บไฟล์">
          <div class="fm-storage-row">
            <span class="fm-muted-strong">พื้นที่เก็บไฟล์</span>
            <span class="flex-grow-1"></span>
            <strong>{{ storage.usage }}</strong>
            <span class="fm-muted">จาก {{ storage.quota }}</span>
          </div>
          <div
            class="fm-meter"
            role="progressbar"
            aria-label="ใช้พื้นที่ไปแล้ว"
            :aria-valuenow="storage.percent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="fm-meter-fill"
              :class="{ 'fm-meter-fill--full': storage.percent >= 90 }"
              :style="{ width: `${storage.percent}%` }"
            ></div>
          </div>
          <div class="fm-storage-row fm-small fm-muted">
            <span>ใช้ไป {{ storage.percent }}%</span>
            <span class="flex-grow-1"></span>
            <span v-if="storage.expiration">หมดอายุ {{ storage.expiration }}</span>
          </div>
        </section>
      </header>

      <div class="fm-toolbar">
        <div class="fm-seg" role="group" aria-label="มุมมอง">
          <button
            type="button"
            :aria-pressed="String(tab === 'active')"
            :class="{ on: tab === 'active' }"
            @click="switchTab('active')"
          >
            <i class="fas fa-folder-open" aria-hidden="true"></i> ไฟล์ทั้งหมด
          </button>
          <button
            type="button"
            :aria-pressed="String(tab === 'trash')"
            :class="{ on: tab === 'trash' }"
            @click="switchTab('trash')"
          >
            <i class="fas fa-trash-alt" aria-hidden="true"></i> ถังขยะ
          </button>
        </div>

        <div class="fm-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model="q"
            type="search"
            aria-label="ค้นหาไฟล์"
            placeholder="ค้นหา DN ชื่อคนไข้ หรือคำอธิบาย"
            @input="onSearchInput"
            @keydown.enter.prevent="reload"
          />
        </div>

        <span class="flex-grow-1 fm-desk"></span>

        <div class="fm-sort">
          <label for="fm-sort">เรียง</label>
          <select id="fm-sort" v-model="sort" @change="reload">
            <option v-for="o in sortOptions" :key="o.value" :value="o.value">
              {{ o.text }}
            </option>
          </select>
        </div>
      </div>

      <div
        v-if="tab === 'active'"
        class="fm-chips"
        role="group"
        aria-label="กรองประเภท"
      >
        <button
          v-for="chip in chips"
          :key="chip.value"
          type="button"
          class="fm-chip"
          :class="{ on: category === chip.value }"
          :aria-pressed="String(category === chip.value)"
          @click="setCategory(chip.value)"
        >
          <span
            v-if="chip.color"
            class="fm-dot"
            :style="{ background: chip.color }"
          ></span>
          {{ chip.label }}
          <span v-if="loaded" class="fm-chip-count">{{ chip.count }}</span>
        </button>
      </div>

      <div v-else-if="loaded && total > 0" class="fm-trash-note">
        <span class="fm-trash-note-icon" aria-hidden="true">
          <i class="fas fa-exclamation-triangle"></i>
        </span>
        <div class="flex-grow-1">
          <strong>ไฟล์ในถังขยะกู้คืนได้</strong>
          <span>ล้างถังขยะหรือลบถาวรแล้ว จะกู้คืนไม่ได้อีก</span>
        </div>
        <button
          type="button"
          class="fm-btn fm-btn--danger-outline"
          :disabled="emptyingTrash"
          @click="emptyTrash"
        >
          <b-spinner v-if="emptyingTrash" small class="mr-1"></b-spinner>
          ล้างถังขยะ
        </button>
      </div>

      <section class="fm-list" :class="{ 'fm-list--trash': tab === 'trash' }">
        <div class="fm-row fm-row--head fm-desk">
          <input
            type="checkbox"
            class="fm-checkbox"
            aria-label="เลือกทุกไฟล์ที่แสดง"
            :checked="allShownSelected"
            :indeterminate.prop="someShownSelected"
            :disabled="items.length === 0"
            @change="toggleAllShown"
          />
          <span></span>
          <span>ไฟล์</span>
          <span>คนไข้</span>
          <span v-if="tab === 'active'">วันที่รักษา</span>
          <span>{{ tab === "trash" ? "ลบเมื่อ" : "อัปโหลดเมื่อ" }}</span>
          <span class="text-right">ขนาด</span>
          <span></span>
        </div>

        <div
          v-for="item in items"
          :key="item.id"
          class="fm-row"
          :class="{ selected: selectedIds.has(item.id), busy: item.isProcessing }"
          @click="onRowClick(item)"
        >
          <label class="fm-check" @click.stop>
            <input
              type="checkbox"
              class="fm-checkbox"
              :checked="selectedIds.has(item.id)"
              :aria-label="`เลือก ${titleOf(item)}`"
              @change="toggle(item)"
            />
          </label>

          <button
            type="button"
            class="fm-thumb"
            :class="`fm-thumb--${kindOf(item)}`"
            :aria-label="`ดู ${titleOf(item)}`"
            @click.stop="onThumbClick(item)"
          >
            <template v-if="kindOf(item) === 'pdf'">PDF</template>
            <template v-else-if="kindOf(item) === 'dicom'">
              <img
                v-if="dicomThumbnails[item.id]"
                :src="dicomThumbnails[item.id]"
                alt=""
              />
              <template v-else>DICOM</template>
            </template>
            <MediaThumb
              v-else-if="kindOf(item) === 'image'"
              :item="item"
              alt=""
              class="fm-thumb-img"
            />
            <i v-else class="far fa-file" aria-hidden="true"></i>
          </button>

          <div class="fm-file">
            <span class="fm-remark">{{ titleOf(item) }}</span>
            <span class="fm-meta">
              <span class="fm-cat" :style="catStyle(item)">
                <i :class="themeOf(item).icon" aria-hidden="true"></i>
                {{ themeOf(item).label || item.category }}
              </span>
              <span class="fm-muted fm-small fm-desk-inline">{{ extOf(item) }}</span>
              <span class="fm-muted fm-small fm-mob">
                {{ tab === "trash" ? formatDate(item.deletedAt) : formatDate(item.creationDt) }}
                · {{ sizeOf(item) }}
              </span>
            </span>
            <span class="fm-who fm-mob">
              {{ item.patient ? `DN ${item.patient.dn} · ${patientName(item.patient)}` : whoWithoutPatient(item) }}
            </span>
          </div>

          <div class="fm-patient fm-desk">
            <template v-if="item.patient">
              <button
                type="button"
                class="fm-link-btn"
                :title="`เปิดประวัติของ DN ${item.patient.dn}`"
                @click.stop="openPatient(item)"
              >
                DN {{ item.patient.dn }}
              </button>
              <span class="fm-muted fm-small fm-ellipsis">{{ patientName(item.patient) }}</span>
            </template>
            <span v-else class="fm-muted fm-small">{{ whoWithoutPatient(item) }}</span>
          </div>

          <span
            v-if="tab === 'active'"
            class="fm-desk fm-small"
            :class="{ 'fm-faint': !item.treatmentDt }"
          >
            {{ item.treatmentDt ? formatDate(item.treatmentDt) : "ไม่ระบุ" }}
          </span>

          <div class="fm-desk fm-when">
            <template v-if="tab === 'trash'">
              <span class="fm-small">{{ formatDate(item.deletedAt) }}</span>
              <span class="fm-muted fm-small">{{ formatTime(item.deletedAt) }}</span>
            </template>
            <template v-else>
              <span class="fm-small">{{ formatDate(item.creationDt) }}</span>
              <span class="fm-muted fm-small">{{ formatTime(item.creationDt) }}</span>
            </template>
          </div>

          <span class="fm-desk fm-small fm-size">{{ sizeOf(item) }}</span>

          <div class="fm-actions" @click.stop>
            <b-spinner
              v-if="item.isProcessing"
              small
              variant="secondary"
              label="กำลังดำเนินการ"
            ></b-spinner>
            <template v-else>
              <div v-if="tab === 'active'" class="fm-desk-flex">
                <button
                  type="button"
                  class="fm-icon-btn"
                  :title="`ดาวน์โหลดเป็น ${downloadNameOf(item)}`"
                  :aria-label="`ดาวน์โหลด ${titleOf(item)}`"
                  @click="download(item)"
                >
                  <i class="fas fa-download" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="fm-icon-btn fm-icon-btn--danger"
                  title="ย้ายไปถังขยะ"
                  :aria-label="`ย้าย ${titleOf(item)} ไปถังขยะ`"
                  @click="moveToTrash([item])"
                >
                  <i class="fas fa-trash-alt" aria-hidden="true"></i>
                </button>
              </div>
              <div v-else class="fm-desk-flex">
                <button
                  type="button"
                  class="fm-btn fm-btn--primary-outline"
                  @click="restore([item])"
                >
                  <i class="fas fa-trash-restore" aria-hidden="true"></i> กู้คืน
                </button>
                <button
                  type="button"
                  class="fm-btn fm-btn--danger-outline"
                  @click="permanentlyDelete([item])"
                >
                  ลบถาวร
                </button>
              </div>

              <b-dropdown
                class="fm-mob"
                right
                no-caret
                variant="link"
                toggle-class="fm-more"
                :aria-label="`ตัวเลือกของ ${titleOf(item)}`"
              >
                <template #button-content>
                  <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
                </template>
                <template v-if="tab === 'active'">
                  <b-dropdown-item v-if="canShareFiles" @click="share([item])">
                    <i class="fas fa-share-square fa-fw mr-2"></i>แชร์
                  </b-dropdown-item>
                  <b-dropdown-item @click="download(item)">
                    <i class="fas fa-download fa-fw mr-2"></i>ดาวน์โหลด
                  </b-dropdown-item>
                  <b-dropdown-item v-if="item.patient" @click="openPatient(item)">
                    <i class="fas fa-user fa-fw mr-2"></i>เปิดประวัติคนไข้
                  </b-dropdown-item>
                  <b-dropdown-item variant="danger" @click="moveToTrash([item])">
                    <i class="fas fa-trash-alt fa-fw mr-2"></i>ย้ายไปถังขยะ
                  </b-dropdown-item>
                </template>
                <template v-else>
                  <b-dropdown-item @click="restore([item])">
                    <i class="fas fa-trash-restore fa-fw mr-2"></i>กู้คืน
                  </b-dropdown-item>
                  <b-dropdown-item variant="danger" @click="permanentlyDelete([item])">
                    <i class="fas fa-times fa-fw mr-2"></i>ลบถาวร
                  </b-dropdown-item>
                </template>
              </b-dropdown>
            </template>
          </div>
        </div>

        <div v-if="loading && items.length === 0" class="fm-state">
          <b-spinner variant="primary"></b-spinner>
          <span>กำลังโหลดไฟล์…</span>
        </div>
        <div v-else-if="loadError" class="fm-state">
          <span>{{ loadError }}</span>
          <button type="button" class="fm-btn fm-btn--primary-outline" @click="retry">
            ลองใหม่
          </button>
        </div>
        <div v-else-if="loaded && items.length === 0" class="fm-state">
          <i class="fas fa-folder-open fm-state-icon" aria-hidden="true"></i>
          <span>{{ emptyText }}</span>
        </div>

        <div ref="sentinel" class="fm-sentinel">
          <template v-if="loadingMore">
            <b-spinner small variant="primary" class="mr-2"></b-spinner>
            กำลังโหลดไฟล์ถัดไป…
          </template>
        </div>
      </section>
    </div>

    <div v-if="selected.length > 0" class="fm-selbar" role="region" aria-label="ไฟล์ที่เลือก">
      <span class="fm-selbar-count">{{ selected.length }}</span>
      <span class="fm-selbar-label">
        ไฟล์ที่เลือก<span v-if="tab === 'active'" class="fm-desk-inline"> · {{ selectedSize }}</span>
      </span>
      <button type="button" class="fm-selbar-link fm-desk" @click="clearSelection">
        ล้างการเลือก
      </button>
      <template v-if="tab === 'active'">
        <button
          v-if="canShareFiles"
          type="button"
          class="fm-selbar-btn fm-selbar-btn--ghost fm-mob"
          @click="share(selected)"
        >
          <i class="fas fa-share-square" aria-hidden="true"></i>
          {{ preparedShare ? "แตะเพื่อแชร์" : "แชร์" }}
        </button>
        <button type="button" class="fm-selbar-btn" @click="downloadSelected">
          <i class="fas fa-download" aria-hidden="true"></i>
          {{ selected.length > 1 ? "ดาวน์โหลด .zip" : "ดาวน์โหลด" }}
        </button>
        <button
          type="button"
          class="fm-selbar-btn fm-selbar-btn--danger"
          aria-label="ย้ายไฟล์ที่เลือกไปถังขยะ"
          @click="moveToTrash(selected)"
        >
          <i class="fas fa-trash-alt" aria-hidden="true"></i>
          <span class="fm-desk-inline">ย้ายไปถังขยะ</span>
        </button>
      </template>
      <template v-else>
        <button type="button" class="fm-selbar-btn" @click="restore(selected)">
          กู้คืน {{ selected.length }} ไฟล์
        </button>
        <button
          type="button"
          class="fm-selbar-btn fm-selbar-btn--danger"
          @click="permanentlyDelete(selected)"
        >
          ลบถาวร
        </button>
      </template>
    </div>

    <Lightbox :index="lightBoxIndex" :items="lightBoxList" @close="resetLightbox" />
    <Dialog ref="Dialog"></Dialog>
    <ProfileCustomerModal ref="profileCustomerModal"></ProfileCustomerModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { debounce } from "lodash";
import Dialog from "@/components/modal/Dialog";
import MediaThumb from "@/components/common/MediaThumb";
import ProfileCustomerModal from "@/components/modal/ProfileCustomer";
import VueLightbox from "vue-lightbox-lite";
import "vue-lightbox-lite/dist/index.css";

import bytesToSize from "@/utils/bytesToSize";
import formatDate from "@/utils/formatDate";
import { isDicomFile, createDicomThumbnail } from "@/utils/daikonUtils";
import { CATEGORY_THEME, categoryTheme } from "@/utils/categoryTheme";
import { mapWithLimit } from "@/utils/mediaUpload";
import { useFileOperations } from "@/composables/useFileOperations";
import {
  PAGE_SIZE,
  SORT_OPTIONS,
  buildManageParams,
  defaultSort,
  manageDownloadName,
  manageZipName,
  patientName,
  withoutFiles,
} from "@/utils/fileManage";

const CHIP_CATEGORIES = [
  "IntraOralPhoto",
  "ExtraOralPhoto",
  "XRay",
  "OpdCard",
  "Document",
  "Template",
];
const THUMB_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const ACTION_CONCURRENCY = 4;
const LOAD_FAILED = "โหลดรายการไฟล์ไม่สำเร็จ";

// Web Share with files: phones and some desktops. Probed with a stand-in file.
const canShareFiles = () => {
  try {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({
        files: [new File([""], "x.jpg", { type: "image/jpeg" })],
      })
    );
  } catch (error) {
    return false;
  }
};

export default {
  name: "FileManage",
  components: { Lightbox: VueLightbox, Dialog, MediaThumb, ProfileCustomerModal },
  data() {
    return {
      tab: "active",
      category: "all",
      q: "",
      sort: defaultSort("active"),

      items: [],
      total: 0,
      categoryCounts: {},
      loaded: false,
      loading: false,
      loadingMore: false,
      reachedEnd: false,
      loadError: "",
      // Each request's number; a response for an older one is dropped.
      requestSeq: 0,
      // Changes only when the list is replaced, not when a page is added:
      // thumbnails still coming for rows on screen must keep coming.
      listGeneration: 0,

      selected: [],
      // Phones show checkboxes only after "เลือก".
      selecting: false,
      emptyingTrash: false,
      canShareFiles: canShareFiles(),
      // Files fetched for the share sheet when the phone wanted a fresh tap.
      preparedShare: null,

      lightBoxIndex: null,
      lightBoxList: [],
      dicomThumbnails: {},
      observer: null,
    };
  },
  computed: {
    ...mapGetters({
      getBranchInfo: "moduleUser/getBranchInfo",
    }),
    sortOptions() {
      return SORT_OPTIONS[this.tab];
    },
    chips() {
      const all = {
        value: "all",
        label: "ทั้งหมด",
        count: Object.values(this.categoryCounts).reduce((sum, n) => sum + n, 0),
      };
      return [all].concat(
        CHIP_CATEGORIES.map(value => ({
          value,
          label: CATEGORY_THEME[value].label,
          color: CATEGORY_THEME[value].color,
          count: this.categoryCounts[value] || 0,
        }))
      );
    },
    storage() {
      const info = this.getBranchInfo || {};
      const quota = Number(info.fileStoreQuota) || 0;
      const usage = Number(info.fileStoreUsage) || 0;
      return {
        quota: bytesToSize(quota),
        usage: bytesToSize(usage),
        percent: quota > 0 ? Math.min(100, Math.round((usage / quota) * 100)) : 0,
        expiration: info.fileStoreExpiration
          ? moment(info.fileStoreExpiration).locale("th").format("D MMM YYYY")
          : "",
      };
    },
    selectedIds() {
      return new Set(this.selected.map(f => f.id));
    },
    selectedSize() {
      return bytesToSize(
        this.selected.reduce((sum, f) => sum + (Number(f.fileSize) || 0), 0)
      );
    },
    allShownSelected() {
      return this.items.length > 0 && this.items.every(f => this.selectedIds.has(f.id));
    },
    someShownSelected() {
      return !this.allShownSelected && this.items.some(f => this.selectedIds.has(f.id));
    },
    emptyText() {
      if (this.q.trim()) return `ไม่พบไฟล์ที่ตรงกับ "${this.q.trim()}"`;
      if (this.tab === "trash") return "ไม่มีไฟล์ในถังขยะ";
      if (this.category !== "all") return "ยังไม่มีไฟล์ในหมวดนี้";
      return "ยังไม่มีไฟล์";
    },
  },
  watch: {
    items(list) {
      this.generateDicomThumbnails(list);
    },
    // Files prepared for sharing belong to the selection they came from.
    selected() {
      this.preparedShare = null;
    },
  },
  created() {
    this.onSearchInput = debounce(this.reload, 300);
  },
  mounted() {
    this.reload();
    if (typeof IntersectionObserver === "function") {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries.some(e => e.isIntersecting)) this.loadMore();
        },
        { rootMargin: "400px" }
      );
      this.observer.observe(this.$refs.sentinel);
    }
  },
  beforeDestroy() {
    this.onSearchInput.cancel();
    if (this.observer) this.observer.disconnect();
    this.requestSeq += 1;
    this.listGeneration += 1;
  },
  methods: {
    ...mapActions({
      fetchClinicMediaFiles: "moduleMediaFile/fetchClinicMediaFiles",
      fetchMediaFiles: "moduleMediaFile/fecthMediaFiles",
      deleteMediaFile: "moduleMediaFile/deleteMediaFile",
      restoreMediaFile: "moduleMediaFile/restoreMediaFile",
      permanentlyDeleteMediaFile: "moduleMediaFile/permanentlyDeleteMediaFile",
      emptyRecycleBin: "moduleMediaFile/emptyRecycleBin",
    }),
    formatDate,
    patientName,
    formatTime(date) {
      return date ? moment(date).format("HH:mm") : "";
    },
    themeOf(item) {
      return categoryTheme(item.category);
    },
    catStyle(item) {
      const theme = this.themeOf(item);
      return { color: theme.color, background: theme.tint };
    },
    titleOf(item) {
      return item.remark || this.themeOf(item).label || item.category;
    },
    whoWithoutPatient(item) {
      return item.category === "Template" ? "ใช้ทั้งคลินิก" : "ไม่ระบุคนไข้";
    },
    kindOf(item) {
      if (item.fileType === "application/pdf") return "pdf";
      if (isDicomFile(item)) return "dicom";
      if (THUMB_TYPES.includes(item.fileType)) return "image";
      return "other";
    },
    extOf(item) {
      if (this.kindOf(item) === "dicom") return "DICOM";
      const sub = String(item.fileType || "").split("/")[1] || "";
      return sub.replace("jpeg", "jpg").toUpperCase();
    },
    sizeOf(item) {
      return bytesToSize(item.fileSize);
    },
    downloadNameOf(item) {
      return manageDownloadName(item);
    },
    route() {
      return {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      };
    },

    // Listing

    async fetchPage(offset, generation) {
      const seq = ++this.requestSeq;
      const params = buildManageParams({
        tab: this.tab,
        category: this.category,
        q: this.q,
        sort: this.sort,
        offset,
        limit: PAGE_SIZE,
      });
      const page = await this.fetchClinicMediaFiles({
        ...this.route(),
        params,
        isCurrent: () => generation === this.listGeneration,
      });
      return seq === this.requestSeq ? page : null;
    },
    async reload() {
      this.onSearchInput.cancel();
      this.loading = true;
      this.loadError = "";
      try {
        const page = await this.fetchPage(0, ++this.listGeneration);
        if (!page) return;
        this.items = page.items;
        this.total = page.total;
        this.categoryCounts = page.categoryCounts;
        this.reachedEnd = page.items.length < PAGE_SIZE || page.items.length >= page.total;
        this.loaded = true;
        // Selections apply to what is on screen.
        const shown = new Set(page.items.map(f => f.id));
        this.selected = this.selected.filter(f => shown.has(f.id));
      } catch (error) {
        console.error("Fetch files error:", error);
        this.loadError = LOAD_FAILED;
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      if (this.loading || this.loadingMore || this.reachedEnd || !this.loaded || this.loadError) {
        return;
      }
      this.loadingMore = true;
      try {
        const page = await this.fetchPage(this.items.length, this.listGeneration);
        if (!page) return;
        const known = new Set(this.items.map(f => f.id));
        this.items = this.items.concat(page.items.filter(f => !known.has(f.id)));
        this.total = page.total;
        this.categoryCounts = page.categoryCounts;
        this.reachedEnd = page.items.length < PAGE_SIZE || this.items.length >= page.total;
      } catch (error) {
        console.error("Fetch more files error:", error);
        this.loadError = LOAD_FAILED;
      } finally {
        this.loadingMore = false;
      }
    },
    retry() {
      if (this.items.length === 0) {
        this.reload();
      } else {
        this.loadError = "";
        this.loadMore();
      }
    },
    switchTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.sort = defaultSort(tab);
      this.category = "all";
      this.selected = [];
      this.items = [];
      this.loaded = false;
      this.reload();
    },
    setCategory(value) {
      if (this.category === value) return;
      this.category = value;
      this.reload();
    },

    // Selection

    toggle(item) {
      if (this.selectedIds.has(item.id)) {
        this.selected = this.selected.filter(f => f.id !== item.id);
      } else {
        this.selected = this.selected.concat(item);
      }
    },
    toggleAllShown() {
      if (this.allShownSelected) {
        this.selected = [];
      } else {
        const more = this.items.filter(f => !this.selectedIds.has(f.id));
        this.selected = this.selected.concat(more);
      }
    },
    clearSelection() {
      this.selected = [];
      this.selecting = false;
    },
    toggleSelecting() {
      if (this.selecting) {
        this.clearSelection();
      } else {
        this.selecting = true;
      }
    },
    onRowClick(item) {
      if (this.selecting) this.toggle(item);
    },
    onThumbClick(item) {
      if (this.selecting) {
        this.toggle(item);
      } else {
        this.preview(item);
      }
    },

    // Viewing

    preview(item) {
      if (isDicomFile(item)) {
        // base64 keeps the presigned URL's security token intact.
        const url = `/dicom-viewer?data=${btoa(item.src)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      const list = this.items.filter(f => !isDicomFile(f));
      this.lightBoxList = list;
      this.lightBoxIndex = list.findIndex(f => f.id === item.id);
    },
    resetLightbox() {
      this.lightBoxIndex = null;
      this.lightBoxList = [];
    },
    openPatient(item) {
      if (!item.patient) return;
      this.$refs.profileCustomerModal.show({}, { id: item.patient.id }, true);
    },
    async generateDicomThumbnails(list) {
      const pending = list.filter(f => isDicomFile(f) && !this.dicomThumbnails[f.id]);
      for (const item of pending) {
        try {
          const thumbnail = await createDicomThumbnail(item.src);
          if (thumbnail) this.$set(this.dicomThumbnails, item.id, thumbnail);
        } catch (error) {
          console.warn("Failed to generate DICOM thumbnail for", item.id, error);
        }
      }
    },

    // Downloading and sharing. Progress shows in the app's transfer toast.

    setDownloadStatus(status) {
      this.$store.commit("moduleMediaFile/setDownloadStatus", status);
    },
    // Presigned URLs expire after 15 minutes; ask the list endpoint the file
    // came from for a fresh one.
    async freshFileUrl(file) {
      const params = { limit: 500, sort: "creationDt", order: "desc", ephemeral: true };
      if (file.patientId) params.patientId = file.patientId;
      else if (file.treatmentId) params.treatmentId = file.treatmentId;
      else if (file.labId) params.labId = file.labId;
      else params.category = file.category;
      try {
        const list = await this.fetchMediaFiles({ ...this.route(), params });
        const match = list.find(f => f.id === file.id);
        return match ? match.src : null;
      } catch (error) {
        return null;
      }
    },
    async download(item) {
      const { downloadFile } = useFileOperations();
      this.setDownloadStatus({ active: true, done: 0, total: 1, percent: 0, phase: "fetch" });
      try {
        await downloadFile(item, {
          fileName: this.downloadNameOf(item),
          refreshUrl: this.freshFileUrl,
          onProgress: this.setDownloadStatus,
        });
      } catch (error) {
        this.$refs.Dialog.showAlert("ดาวน์โหลดไม่สำเร็จ", error.message);
      } finally {
        this.setDownloadStatus({ active: false });
      }
    },
    async downloadSelected() {
      const files = this.selected;
      if (files.length === 0) return;
      const { bulkDownload } = useFileOperations();
      this.setDownloadStatus({
        active: true,
        done: 0,
        total: files.length,
        percent: 0,
        phase: "fetch",
      });
      try {
        const { failed } = await bulkDownload(files, {
          fileNameOf: manageDownloadName,
          zipName: manageZipName(files),
          refreshUrl: this.freshFileUrl,
          onProgress: this.setDownloadStatus,
        });
        if (failed.length > 0) {
          this.$refs.Dialog.showAlert(
            "ดาวน์โหลดไม่ครบ",
            `ไม่สามารถดาวน์โหลด: ${failed.join(", ")}`
          );
        }
      } catch (error) {
        this.$refs.Dialog.showAlert(
          "ดาวน์โหลดไม่สำเร็จ",
          error.message || "กรุณาลองใหม่อีกครั้ง"
        );
      } finally {
        this.setDownloadStatus({ active: false });
      }
    },
    // Fetching can outlast the tap that asked for it; the browser then
    // refuses to open the share sheet, and the button offers a new tap.
    async share(files) {
      if (this.preparedShare) {
        await this.openShareSheet(false);
        return;
      }
      if (files.length === 0) return;
      const { fetchFiles } = useFileOperations();
      this.setDownloadStatus({ active: true, done: 0, total: files.length, percent: 0, phase: "fetch" });
      let fetched;
      try {
        const result = await fetchFiles(files, {
          fileNameOf: manageDownloadName,
          refreshUrl: this.freshFileUrl,
          onProgress: this.setDownloadStatus,
        });
        fetched = result.files;
        if (fetched.length === 0) throw new Error("nothing fetched");
        if (result.failed.length > 0) {
          this.$refs.Dialog.showAlert("โหลดไฟล์ไม่ครบ", `แชร์ได้ไม่ครบ: ${result.failed.join(", ")}`);
        }
      } catch (error) {
        this.$refs.Dialog.showAlert("แชร์ไม่สำเร็จ", "โหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        return;
      } finally {
        this.setDownloadStatus({ active: false });
      }
      this.preparedShare = fetched;
      await this.openShareSheet(true);
    },
    async openShareSheet(firstTry) {
      const files = this.preparedShare;
      if (!files) return;
      try {
        await navigator.share({ files });
        this.preparedShare = null;
      } catch (error) {
        if (error.name === "NotAllowedError" && firstTry) return;
        this.preparedShare = null;
        if (error.name === "AbortError") return;
        this.$refs.Dialog.showAlert(
          "แชร์ไม่สำเร็จ",
          "อุปกรณ์นี้แชร์ไฟล์ชุดนี้ไม่ได้ ลองบันทึกลงเครื่องแทน"
        );
      }
    },

    // Trash, restore, delete

    /**
     * Runs an action on each file, a few at a time, then takes the ones that
     * worked out of the list, the counts and the selection.
     *
     * @returns {Promise<{ done: Object[], failed: Object[] }>}
     */
    async runOnFiles(files, action) {
      const done = [];
      const failed = [];
      await mapWithLimit(files, ACTION_CONCURRENCY, async file => {
        this.$set(file, "isProcessing", true);
        try {
          await action({ ...this.route(), id: file.id });
          done.push(file);
        } catch (error) {
          console.error(`File ${file.id} action failed:`, error);
          failed.push(file);
        } finally {
          this.$set(file, "isProcessing", false);
        }
      });
      if (done.length > 0) {
        const next = withoutFiles(
          { items: this.items, total: this.total, categoryCounts: this.categoryCounts },
          done
        );
        this.items = next.items;
        this.total = next.total;
        this.categoryCounts = next.categoryCounts;
        const gone = new Set(done.map(f => f.id));
        this.selected = this.selected.filter(f => !gone.has(f.id));
        if (this.selected.length === 0) this.selecting = false;
        // A page emptied by the action should not strand the rest.
        if (this.items.length < PAGE_SIZE && !this.reachedEnd) this.loadMore();
      }
      return { done, failed };
    },
    notify(text) {
      this.$bvToast.toast(text, {
        title: "สำเร็จ",
        variant: "success",
        solid: true,
        autoHideDelay: 3000,
        toaster: "b-toaster-bottom-left",
      });
    },
    reportFailures(failed, what) {
      if (failed.length === 0) return;
      this.$refs.Dialog.showAlert(
        `${what}ไม่สำเร็จ ${failed.length} ไฟล์`,
        failed.map(f => this.titleOf(f)).join(", ")
      );
    },
    async moveToTrash(files) {
      const list = files.slice();
      const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
        list.length === 1 ? "ย้ายไฟล์นี้ไปถังขยะ ?" : `ย้าย ${list.length} ไฟล์ไปถังขยะ ?`,
        "กู้คืนได้จากแท็บถังขยะ"
      );
      if (!isConfirmed) return;
      const { done, failed } = await this.runOnFiles(list, this.deleteMediaFile);
      if (done.length) this.notify(`ย้ายไปถังขยะแล้ว ${done.length} ไฟล์`);
      this.reportFailures(failed, "ย้ายไปถังขยะ");
    },
    async restore(files) {
      const { done, failed } = await this.runOnFiles(files.slice(), this.restoreMediaFile);
      if (done.length) this.notify(`กู้คืนแล้ว ${done.length} ไฟล์`);
      this.reportFailures(failed, "กู้คืน");
    },
    async permanentlyDelete(files) {
      const list = files.slice();
      const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
        list.length === 1 ? "ลบไฟล์นี้ถาวร ?" : `ลบ ${list.length} ไฟล์ถาวร ?`,
        "ลบถาวรแล้วจะกู้คืนไม่ได้อีก",
        "warning",
        "ยืนยัน",
        "ยกเลิก"
      );
      if (!isConfirmed) return;
      const { isConfirmed: sure } = await this.$refs.Dialog.fire({
        title: "ยืนยันการลบถาวร",
        text: "การลบถาวรไม่สามารถกู้คืนไฟล์ได้ คุณแน่ใจหรือไม่?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ลบถาวร",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#b42318",
      });
      if (!sure) return;
      const { done, failed } = await this.runOnFiles(list, this.permanentlyDeleteMediaFile);
      if (done.length) this.notify(`ลบถาวรแล้ว ${done.length} ไฟล์`);
      this.reportFailures(failed, "ลบ");
    },
    async emptyTrash() {
      const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
        "ล้างถังขยะทั้งหมด ?",
        "ไฟล์ทุกไฟล์ในถังขยะจะถูกลบถาวร กู้คืนไม่ได้อีก",
        "warning",
        "ยืนยัน",
        "ยกเลิก"
      );
      if (!isConfirmed) return;
      const { value } = await this.$refs.Dialog.fire({
        title: "ยืนยันการล้างถังขยะ",
        text: 'พิมพ์คำว่า "ลบทั้งหมด" เพื่อยืนยัน',
        input: "text",
        inputPlaceholder: "ลบทั้งหมด",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#b42318",
        inputValidator: text =>
          text !== "ลบทั้งหมด" ? 'กรุณาพิมพ์คำว่า "ลบทั้งหมด" ให้ถูกต้อง' : undefined,
      });
      if (value !== "ลบทั้งหมด") return;
      this.emptyingTrash = true;
      try {
        await this.emptyRecycleBin(this.route());
        this.selected = [];
        this.notify("ล้างถังขยะแล้ว");
        await this.reload();
      } catch (error) {
        console.error(error);
        this.$refs.Dialog.showAlert("ล้างถังขยะไม่สำเร็จ", "กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.emptyingTrash = false;
      }
    },
  },
};
</script>

<style scoped>
.fm {
  --fm-ink: #101828;
  --fm-muted: #5d6679;
  --fm-soft: #3d4557;
  --fm-line: #e8eaef;
  --fm-line-strong: #d7dbe3;
  --fm-blue: #0b63c5;
  --fm-red: #8f1d14;
  background: #f4f5f7;
  color: var(--fm-ink);
  min-height: 100%;
}

.fm-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 28px 120px;
  max-width: 1440px;
}

.fm-muted {
  color: var(--fm-muted);
}
.fm-muted-strong {
  color: var(--fm-soft);
  font-size: 13px;
}
.fm-faint {
  color: #8a93a3;
}
.fm-small {
  font-size: 13px;
}
.fm-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Header */
.fm-head {
  display: flex;
  align-items: flex-end;
  gap: 24px;
}
.fm-title {
  flex-grow: 1;
  min-width: 0;
}
.fm-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.fm-title h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.fm-title p {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--fm-muted);
}
.fm-pill {
  padding: 2px 10px;
  border-radius: 999px;
  background: #e7eaf0;
  font-size: 13px;
  font-weight: 600;
  color: var(--fm-soft);
  white-space: nowrap;
}
.fm-storage {
  width: 360px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid var(--fm-line);
  border-radius: 16px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.fm-storage-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.fm-meter {
  height: 8px;
  border-radius: 4px;
  background: #eef0f3;
  overflow: hidden;
}
.fm-meter-fill {
  height: 100%;
  border-radius: 4px;
  background: var(--fm-blue);
}
.fm-meter-fill--full {
  background: #b42318;
}

/* Toolbar */
.fm-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.fm-seg {
  display: flex;
  padding: 4px;
  gap: 2px;
  background: #e7eaf0;
  border-radius: 999px;
  flex-shrink: 0;
}
.fm-seg button {
  height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 14px;
  color: var(--fm-soft);
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.fm-seg button.on {
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  font-weight: 600;
  color: var(--fm-ink);
}
.fm-search {
  position: relative;
  flex-grow: 1;
  max-width: 520px;
}
.fm-search i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--fm-muted);
  font-size: 14px;
}
.fm-search input {
  width: 100%;
  height: 42px;
  border: 1px solid var(--fm-line-strong);
  border-radius: 999px;
  padding: 0 16px 0 40px;
  font-size: 14px;
  background: #fff;
}
.fm-search input:focus,
.fm-sort select:focus {
  outline: none;
  border-color: var(--fm-blue);
  box-shadow: 0 0 0 3px rgba(11, 99, 197, 0.15);
}
.fm-sort {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.fm-sort label {
  margin: 0;
  font-size: 13px;
  color: var(--fm-muted);
}
.fm-sort select {
  height: 42px;
  border: 1px solid var(--fm-line-strong);
  border-radius: 999px;
  padding: 0 14px;
  font-size: 14px;
  background: #fff;
  color: var(--fm-ink);
}

/* Category chips */
.fm-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.fm-chip {
  height: 36px;
  padding: 0 8px 0 14px;
  border-radius: 999px;
  border: 1px solid var(--fm-line-strong);
  background: #fff;
  color: var(--fm-ink);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.fm-chip.on {
  border-color: var(--fm-ink);
  background: var(--fm-ink);
  color: #fff;
}
.fm-dot {
  width: 8px;
  height: 8px;
  border-radius: 4px;
}
.fm-chip-count {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  background: #f1f3f6;
  color: var(--fm-soft);
}
.fm-chip.on .fm-chip-count {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.fm-trash-note {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 14px;
  background: #fff7eb;
  border: 1px solid #f5dcb3;
  color: #6b4a1f;
  font-size: 13px;
}
.fm-trash-note strong {
  display: block;
  font-size: 14px;
  color: #5c3200;
}
.fm-trash-note-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #fdebc8;
  color: #8a4b05;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* List */
.fm-list {
  background: #fff;
  border: 1px solid var(--fm-line);
  border-radius: 18px;
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
/* The file column keeps a real width; below that the list scrolls sideways
   instead of letting the name, category and type pile onto the patient column. */
.fm-row {
  display: grid;
  grid-template-columns: 24px 56px minmax(220px, 1fr) minmax(130px, 200px) 110px 120px 80px 96px;
  min-width: 960px;
  gap: 14px;
  align-items: center;
  min-height: 76px;
  padding: 10px 20px;
  border-bottom: 1px solid #f1f3f6;
}
.fm-list--trash .fm-row {
  grid-template-columns: 24px 56px minmax(220px, 1fr) minmax(130px, 200px) 130px 80px 200px;
}
.fm-row:hover {
  background: #f8f9fb;
}
.fm-row.selected {
  background: #eef5fe;
}
.fm-row.busy {
  opacity: 0.6;
}
.fm-row--head {
  min-height: 46px;
  font-size: 12px;
  font-weight: 600;
  color: var(--fm-muted);
}
.fm-row--head:hover {
  background: transparent;
}
.fm-check {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.fm-checkbox {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--fm-blue);
  cursor: pointer;
}
.fm-thumb {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  padding: 0;
  border: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef0f3;
  font-size: 11px;
  font-weight: 700;
}
.fm-thumb img,
.fm-thumb ::v-deep .fm-thumb-img,
.fm-thumb ::v-deep .media-thumb-placeholder {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.fm-thumb--pdf {
  background: #f4f5f7;
  border: 1px solid #e3e6ec;
  color: #b42318;
}
.fm-thumb--dicom {
  background: #1a1f2a;
  color: #aeb6c4;
  font-size: 9px;
  letter-spacing: 0.08em;
}
.fm-thumb--other {
  color: var(--fm-muted);
  font-size: 20px;
}
.fm-file {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.fm-remark {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fm-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.fm-cat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.fm-patient,
.fm-when {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.fm-size {
  text-align: right;
  color: var(--fm-soft);
}
.fm-link-btn {
  align-self: flex-start;
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--fm-blue);
}
.fm-link-btn:hover {
  text-decoration: underline;
}
.fm-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.fm-desk-flex {
  display: flex;
  gap: 6px;
}
.fm-icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 20px;
  border: 1px solid #e3e6ec;
  background: #fff;
  color: var(--fm-blue);
  display: flex;
  align-items: center;
  justify-content: center;
}
.fm-icon-btn:hover {
  background: var(--fm-blue);
  border-color: var(--fm-blue);
  color: #fff;
}
.fm-icon-btn--danger {
  color: var(--fm-red);
}
.fm-icon-btn--danger:hover {
  background: var(--fm-red);
  border-color: var(--fm-red);
}
.fm-btn {
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.fm-btn--primary-outline {
  border: 1px solid #c9dcf5;
  color: var(--fm-blue);
}
.fm-btn--danger-outline {
  border: 1px solid #f0c6c1;
  color: var(--fm-red);
}
.fm-btn:disabled {
  opacity: 0.6;
}
.fm-state {
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--fm-muted);
  font-size: 14px;
}
.fm-state-icon {
  font-size: 2.4rem;
  color: #c3c8d2;
}
.fm-sentinel {
  min-height: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--fm-muted);
}
.fm-sentinel:not(:empty) {
  height: 52px;
}

/* Selection bar */
.fm-selbar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 1030;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px 8px 10px;
  background: var(--fm-ink);
  color: #fff;
  border-radius: 999px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.35);
  white-space: nowrap;
}
.fm-selbar-count {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 15px;
  background: var(--fm-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.fm-selbar-label {
  font-size: 14px;
  font-weight: 600;
  padding: 0 6px 0 2px;
}
.fm-selbar-link {
  height: 38px;
  padding: 0 10px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #bcd6f7;
  font-size: 13px;
}
.fm-selbar-btn {
  height: 38px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: var(--fm-ink);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.fm-selbar-btn--ghost {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.fm-selbar-btn--danger {
  background: #3a1f1d;
  color: #ffb4ab;
}

/* Desktop / phone parts */
.fm-mob {
  display: none !important;
}
.fm-desk-inline {
  display: inline;
}

@media (max-width: 767.98px) {
  .fm-desk,
  .fm-desk-flex,
  .fm-desk-inline {
    display: none !important;
  }
  .fm-mob {
    display: inline-flex !important;
  }
  span.fm-mob,
  .fm-who.fm-mob {
    display: block !important;
  }
  .fm-page {
    padding: 14px 14px 120px;
    gap: 12px;
  }
  .fm-head {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .fm-title h1 {
    font-size: 24px;
  }
  .fm-title p {
    display: none;
  }
  .fm-storage {
    width: auto;
    padding: 12px 14px;
  }
  .fm-toolbar {
    flex-wrap: wrap;
    gap: 10px;
  }
  .fm-seg {
    width: 100%;
  }
  .fm-seg button {
    flex: 1 1 0;
    justify-content: center;
    height: 40px;
  }
  .fm-search {
    max-width: none;
    flex-basis: 100%;
  }
  .fm-search input {
    height: 44px;
    font-size: 16px;
  }
  .fm-sort label {
    display: none;
  }
  .fm-sort select {
    height: 36px;
    font-size: 13px;
  }
  .fm-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    margin-right: -14px;
    padding-right: 14px;
    scrollbar-width: none;
  }
  .fm-trash-note .fm-btn {
    flex-shrink: 0;
  }
  .fm-row,
  .fm-list--trash .fm-row {
    display: flex;
    min-width: 0;
    gap: 12px;
    min-height: 72px;
    padding: 10px 4px 10px 12px;
  }
  .fm-check {
    display: none;
    width: 28px;
    flex-shrink: 0;
  }
  .fm--selecting .fm-check {
    display: flex;
  }
  .fm-checkbox {
    width: 22px;
    height: 22px;
  }
  .fm-thumb {
    flex-shrink: 0;
  }
  .fm-file {
    flex-grow: 1;
    gap: 3px;
  }
  .fm-meta {
    gap: 6px;
  }
  .fm-meta .fm-small {
    font-size: 11px;
    white-space: nowrap;
  }
  .fm-cat {
    height: 18px;
    font-size: 11px;
    padding: 0 7px;
  }
  .fm-who {
    font-size: 12px;
    color: var(--fm-soft);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fm-actions {
    flex-shrink: 0;
    width: 44px;
    justify-content: center;
  }
  .fm-actions ::v-deep .fm-more {
    width: 44px;
    height: 44px;
    padding: 0;
    color: var(--fm-soft);
  }
  .fm-link-btn.fm-mob {
    height: 44px;
    align-items: center;
    font-size: 15px;
  }
  .fm-selbar {
    left: 0;
    right: 0;
    bottom: 0;
    transform: none;
    border-radius: 18px 18px 0 0;
    padding: 12px 14px calc(14px + env(safe-area-inset-bottom));
    gap: 8px;
  }
  .fm-selbar-label {
    flex-grow: 1;
  }
  .fm-selbar-btn {
    height: 44px;
    padding: 0 14px;
  }
}
</style>
